interface LifestyleCreepInput {
  startingSalary: number
  currentSalary: number
  yearsWorking: number
  startingMonthlyExpenses: number
  currentMonthlyExpenses: number
  investmentReturnRate: number
}

interface YearlySnapshot {
  year: number
  actualWealth: number
  disciplinedWealth: number
  actualSavings: number
  disciplinedSavings: number
}

export function calculateLifestyleCreep(input: LifestyleCreepInput) {
  const {
    startingSalary,
    currentSalary,
    yearsWorking,
    startingMonthlyExpenses,
    currentMonthlyExpenses,
    investmentReturnRate,
  } = input

  const totalRaises = currentSalary - startingSalary
  const annualRaiseRate = yearsWorking > 0
    ? (Math.pow(currentSalary / Math.max(startingSalary, 1), 1 / yearsWorking) - 1) * 100
    : 0

  const startingAnnualExpenses = startingMonthlyExpenses * 12
  const currentAnnualExpenses = currentMonthlyExpenses * 12
  const spendingIncrease = currentAnnualExpenses - startingAnnualExpenses

  const startingSavingsRate = startingSalary > 0
    ? ((startingSalary - startingAnnualExpenses) / startingSalary) * 100
    : 0
  const currentSavingsRate = currentSalary > 0
    ? ((currentSalary - currentAnnualExpenses) / currentSalary) * 100
    : 0

  // Calculate what savings rate would be if spending stayed flat
  const potentialSavingsRate = currentSalary > 0
    ? ((currentSalary - startingAnnualExpenses) / currentSalary) * 100
    : 0

  const creepRate = startingAnnualExpenses > 0
    ? ((currentAnnualExpenses - startingAnnualExpenses) / startingAnnualExpenses) * 100
    : 0

  // Build year-by-year projection
  const monthlyReturn = investmentReturnRate / 100 / 12
  const timeline: YearlySnapshot[] = []

  let actualWealth = 0
  let disciplinedWealth = 0

  for (let year = 1; year <= yearsWorking; year++) {
    // Linearly interpolate salary growth for each year
    const yearFraction = year / yearsWorking
    const yearlySalary = startingSalary + (currentSalary - startingSalary) * yearFraction

    // Actual spending grows linearly too
    const yearlyActualExpenses = startingAnnualExpenses + (currentAnnualExpenses - startingAnnualExpenses) * yearFraction
    const yearlyActualSavings = Math.max(0, yearlySalary - yearlyActualExpenses)

    // Disciplined: spending stays at starting level
    const yearlyDisciplinedSavings = Math.max(0, yearlySalary - startingAnnualExpenses)

    // Compound monthly
    const monthlySavingsActual = yearlyActualSavings / 12
    const monthlySavingsDisciplined = yearlyDisciplinedSavings / 12

    for (let m = 0; m < 12; m++) {
      actualWealth = actualWealth * (1 + monthlyReturn) + monthlySavingsActual
      disciplinedWealth = disciplinedWealth * (1 + monthlyReturn) + monthlySavingsDisciplined
    }

    timeline.push({
      year,
      actualWealth: Math.round(actualWealth),
      disciplinedWealth: Math.round(disciplinedWealth),
      actualSavings: Math.round(yearlyActualSavings),
      disciplinedSavings: Math.round(yearlyDisciplinedSavings),
    })
  }

  const lostWealth = Math.max(0, Math.round(disciplinedWealth - actualWealth))

  return {
    totalRaises: Math.round(totalRaises),
    annualRaiseRate: Math.round(annualRaiseRate * 10) / 10,
    spendingIncrease: Math.round(spendingIncrease),
    startingSavingsRate: Math.round(startingSavingsRate * 10) / 10,
    currentSavingsRate: Math.round(currentSavingsRate * 10) / 10,
    potentialSavingsRate: Math.round(potentialSavingsRate * 10) / 10,
    creepRate: Math.round(creepRate * 10) / 10,
    lostWealth,
    actualWealth: Math.round(actualWealth),
    disciplinedWealth: Math.round(disciplinedWealth),
    timeline,
  }
}
